import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import {
  confirmReview,
  getReview,
  modifyReview,
  rejectReview,
} from '@/api/review';
import { askQuestion, getQAHistory } from '@/api/qa';
import { AiLoading } from '@/components/ui/ai-loading/ai-loading';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import { ScoreRing } from '@/components/ui/score-ring/score-ring';
import { EvidenceList } from '@/components/review/evidence-list/evidence-list';
import { useAuthStore } from '@/stores/authStore';
import type { QuestionDetail } from '@/types';

import styles from './review-detail.module.css';

const isObjective = (t?: string | null) => t === 'choice' || t === 'fill_blank';
const letterOf = (i: number) => String.fromCharCode(65 + i);

export function ReviewDetailPage() {
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const id = Number(submissionId);
  const queryClient = useQueryClient();
  const isTeacher = user?.role === 'teacher';

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [modifyOpen, setModifyOpen] = useState(false);
  const [modifyScore, setModifyScore] = useState<number | ''>('');
  const [modifyComment, setModifyComment] = useState('');

  const [question, setQuestion] = useState('');
  const [qaLoading, setQaLoading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['review', id],
    queryFn: () => getReview(id),
    enabled: !!id,
  });

  const { data: qaData } = useQuery({
    queryKey: ['qa', id],
    queryFn: () => getQAHistory(id),
    enabled: !!id && !isTeacher,
  });

  if (isLoading)
    return (
      <AiLoading
        title="AI 批阅详情加载中"
        hint={['正在调取批阅结果…', '正在整理证据链…', '正在核对分项得分…']}
      />
    );
  if (!data) return <div className={styles.loading}>评阅数据不存在</div>;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['review', id] });
    queryClient.invalidateQueries({ queryKey: ['reviewQueue'] });
  };

  const handleConfirm = async () => {
    setActionError('');
    setActionLoading(true);
    try {
      await confirmReview(id, data.evidences);
      refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '操作失败');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setActionError('请填写驳回原因');
      return;
    }
    setActionError('');
    setActionLoading(true);
    try {
      await rejectReview(id, rejectReason.trim());
      setRejectOpen(false);
      setRejectReason('');
      refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '操作失败');
    } finally {
      setActionLoading(false);
    }
  };

  const handleModify = async () => {
    if (modifyScore === '' || Number.isNaN(Number(modifyScore))) {
      setActionError('请输入有效分数');
      return;
    }
    setActionError('');
    setActionLoading(true);
    try {
      await modifyReview(id, {
        score: Number(modifyScore),
        evidences: data.evidences,
        comment: modifyComment || undefined,
      });
      setModifyOpen(false);
      refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '操作失败');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAsk = async () => {
    if (!question.trim()) return;
    setQaLoading(true);
    try {
      await askQuestion({ submission_id: id, question: question.trim() });
      setQuestion('');
      queryClient.invalidateQueries({ queryKey: ['qa', id] });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '追问失败');
    } finally {
      setQaLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>批阅详情</h1>
        <StatusTagSafe status={data.status} />
      </header>

      <div className={styles.layout}>
        <Card className={styles.contentCard}>
          <h2 className={styles.sectionTitle}>提交内容</h2>
          <pre className={styles.content}>{data.content || '（无文本内容）'}</pre>
        </Card>

        {!isTeacher && data.reject_reason && (
          <Card className={styles.commentCard}>
            <h2 className={styles.sectionTitle}>老师打回原因</h2>
            <p className={styles.comment}>{data.reject_reason}</p>
          </Card>
        )}

        <div className={styles.panel}>
          <Card className={styles.scoreCard}>
            <div className={styles.scoreHeader}>
              <ScoreRing score={data.score} maxScore={data.max_score} />
              <div className={styles.scoreInfo}>
                <div className={styles.scoreLabel}>
                  {isObjective(data.job_type) ? '自动批改' : 'AI 初评'}
                </div>
                <div className={styles.confidence}>
                  置信度：
                  {data.confidence === 'high'
                    ? '高'
                    : data.confidence === 'medium'
                      ? '中'
                      : '低'}
                </div>
              </div>
            </div>
          </Card>

          {isObjective(data.job_type) && data.details && data.details.length > 0 && (
            <ObjectiveBreakdown details={data.details} />
          )}

          <Card className={styles.dimensionsCard}>
            <h2 className={styles.sectionTitle}>分项得分</h2>
            <ul className={styles.dimensions}>
              {data.dimensions.map((dim) => (
                <li key={dim.name} className={styles.dimensionRow}>
                  <span>{dim.name}</span>
                  <span className={styles.dimScore}>
                    {dim.score}/{dim.max_score}
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          {!isObjective(data.job_type) && (
            <Card className={styles.evidenceCard}>
              <h2 className={styles.sectionTitle}>证据链</h2>
              <EvidenceList evidences={data.evidences} />
            </Card>
          )}

          {data.comment && (
            <Card className={styles.commentCard}>
              <h2 className={styles.sectionTitle}>教师评语</h2>
              <p className={styles.comment}>{data.comment}</p>
            </Card>
          )}

          {isTeacher && (
            <div className={styles.actions}>
              {actionError && <div className={styles.errorText}>{actionError}</div>}
              <div className={styles.actionRow}>
                <Button onClick={handleConfirm} loading={actionLoading} disabled={data.status === 'confirmed'}>
                  确认通过
                </Button>
                <Button variant="secondary" onClick={() => setRejectOpen((v) => !v)} disabled={actionLoading}>
                  驳回
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setModifyOpen((v) => !v);
                    setModifyScore(data.score);
                    setModifyComment(data.comment || '');
                  }}
                  disabled={actionLoading}
                >
                  修改评分
                </Button>
              </div>

              {rejectOpen && (
                <div className={styles.editPanel}>
                  <label className={styles.label}>
                    驳回原因
                    <textarea
                      className={styles.textarea}
                      placeholder="请填写驳回原因，学生将重新提交"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                    />
                  </label>
                  <div className={styles.actionRow}>
                    <Button onClick={handleReject} loading={actionLoading}>
                      提交驳回
                    </Button>
                    <Button variant="secondary" onClick={() => setRejectOpen(false)}>
                      取消
                    </Button>
                  </div>
                </div>
              )}

              {modifyOpen && (
                <div className={styles.editPanel}>
                  <label className={styles.label}>
                    总分（满分 {data.max_score}）
                    <input
                      className={styles.input}
                      type="number"
                      value={modifyScore}
                      onChange={(e) =>
                        setModifyScore(e.target.value === '' ? '' : Number(e.target.value))
                      }
                    />
                  </label>
                  <label className={styles.label}>
                    评语
                    <textarea
                      className={styles.textarea}
                      value={modifyComment}
                      onChange={(e) => setModifyComment(e.target.value)}
                    />
                  </label>
                  <div className={styles.actionRow}>
                    <Button onClick={handleModify} loading={actionLoading}>
                      保存修改
                    </Button>
                    <Button variant="secondary" onClick={() => setModifyOpen(false)}>
                      取消
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!isTeacher && (
        <Card className={styles.qaPanel}>
          <div className={styles.qaHeaderRow}>
            <h2 className={styles.sectionTitle}>向老师追问</h2>
            <Button
              size="small"
              variant="secondary"
              onClick={() => navigate(`/feedback?submission=${id}`)}
            >
              对 AI 判分有异议？发起申诉
            </Button>
          </div>
          <div className={styles.qaList}>
            {qaData?.map((q) => (
              <div key={q.id} className={styles.qaItem}>
                <div className={styles.qaQuestion}>问：{q.question}</div>
                <div className={styles.qaAnswer}>答：{q.answer}</div>
              </div>
            ))}
            {qaData && qaData.length === 0 && !qaLoading && (
              <div className={styles.empty}>暂无追问记录，对评语有疑问可发起追问</div>
            )}
            {qaLoading && (
              <AiLoading
                compact
                title="AI 正在思考你的追问"
                hint={['正在分析你的问题…', '正在结合批阅结果作答…']}
              />
            )}
          </div>
          {actionError && <div className={styles.errorText}>{actionError}</div>}
          <div className={styles.qaInputRow}>
            <textarea
              className={styles.textarea}
              placeholder="对评语或评分有疑问？向老师发起追问"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
            <Button onClick={handleAsk} loading={qaLoading} disabled={!question.trim()}>
              发送追问
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function formatAnswer(
  value: string | string[] | null,
  options: string[],
  multiple: boolean,
): string {
  if (value === null || value === undefined || value === '') return '（空）';
  if (Array.isArray(value)) {
    if (value.length === 0) return '（空）';
    return value
      .map((v) => {
        const idx = options.findIndex((_, i) => letterOf(i) === v);
        return idx >= 0 ? `${v}. ${options[idx]}` : v;
      })
      .join('，');
  }
  if (multiple || options.length > 0) {
    const idx = options.findIndex((_, i) => letterOf(i) === value);
    return idx >= 0 ? `${value}. ${options[idx]}` : value;
  }
  return value;
}

function formatCorrect(value: string | string[] | null, type: string): string {
  if (value === null || value === undefined) return '（空）';
  if (type === 'fill_blank') {
    const list = Array.isArray(value) ? value : [value];
    return list.map((a) => String(a).replace(/\|/g, ' / ')).join('；');
  }
  if (Array.isArray(value)) return value.join('，');
  return value;
}

function ObjectiveBreakdown({ details }: { details: QuestionDetail[] }) {
  return (
    <Card className={styles.dimensionsCard}>
      <h2 className={styles.sectionTitle}>逐题批改结果</h2>
      <ul className={styles.qBreakList}>
        {details.map((d, i) => (
          <li key={d.id} className={styles.qBreakRow}>
            <div className={styles.qBreakStem}>
              <span className={styles.qBreakIndex}>{i + 1}</span>
              <span className={styles.qBreakText}>{d.stem}</span>
              <span className={d.is_correct ? styles.qRight : styles.qWrong}>
                {d.is_correct ? '✓ 答对' : '✗ 答错'} {d.earned}/{d.max_score}
              </span>
            </div>
            <div className={styles.qBreakAnswers}>
              <span>
                你的答案：
                {formatAnswer(d.student_answer, d.options, d.type === 'multiple_choice')}
              </span>
              <span>
                正确答案：{formatCorrect(d.correct_answer, d.type)}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function StatusTagSafe({ status }: { status: string }) {
  return <span className={styles.statusBadge}>{status}</span>;
}
